import { type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MinimalSkipLink } from '@ovasabi/ui-minimal'
import { PageTransition } from '../../styles/PageTransition'
import { LandingStyle as Style } from './LandingStyle'

interface PublicNavProps {
  extra?: ReactNode
}

export const PublicNav = ({ extra }: PublicNavProps) => (
  <Style.Nav>
    <Style.NavInner>
      <Style.Brand to="/" aria-label="ChooseChow home">
        <picture>
          <source srcSet="/brand/choosechow-wordmark.webp" type="image/webp" />
          <img
            src="/brand/choosechow-wordmark.webp"
            width="960"
            height="138"
            alt="ChooseChow"
          />
        </picture>
      </Style.Brand>
      {extra}
      <Style.NavLinks aria-label="Main navigation">
        <Link to="/kitchens">Kitchens</Link>
        <Link to="/menus">Menus</Link>
        <Link to="/dishes">Dishes</Link>
        <Link to="/login">Sign in</Link>
        <Style.Cta to="/register?role=customer">Get started</Style.Cta>
      </Style.NavLinks>
    </Style.NavInner>
  </Style.Nav>
)

export const PublicFooter = () => (
  <Style.Footer>
    <Style.FooterInner>
      <Style.FooterTop>
        <Style.FooterBrandCol>
          <img src="/brand/choosechow-lockup.webp" alt="ChooseChow" />
          <p>
            Homemade food, authentic regional flavours, prepared fresh by local
            kitchens.
          </p>
        </Style.FooterBrandCol>
        <Style.FooterNavCol>
          <h4>Explore</h4>
          <nav aria-label="Explore food">
            <Link to="/kitchens">Browse kitchens</Link>
            <Link to="/menus">Chef menus</Link>
            <Link to="/dishes">Explore dishes</Link>
            <Link to="/plans">Meal plans</Link>
          </nav>
        </Style.FooterNavCol>
        <Style.FooterNavCol>
          <h4>Chefs & Makers</h4>
          <nav aria-label="For chefs">
            <Link to="/register?role=chef">Sell your cooking</Link>
            <Link to="/guidelines">Kitchen guidelines</Link>
            <a href="mailto:chefs@choosechow.com">Chef support</a>
          </nav>
        </Style.FooterNavCol>
        <Style.FooterNavCol>
          <h4>Company</h4>
          <nav aria-label="Company links">
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms of service</Link>
            <Link to="/delete-account">Delete your data</Link>
            <a href="mailto:support@choosechow.com">Contact us</a>
          </nav>
        </Style.FooterNavCol>
      </Style.FooterTop>
      <Style.FooterBottom>
        <span>
          © {new Date().getFullYear()} ChooseChow · Homemade food, ready for pickup.
        </span>
        <Style.FooterLegal>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/guidelines">Guidelines</Link>
          <span>Abuja, Nigeria</span>
        </Style.FooterLegal>
      </Style.FooterBottom>
    </Style.FooterInner>
  </Style.Footer>
)

export const PublicShell = ({
  children,
  navExtra,
}: {
  children: ReactNode
  navExtra?: ReactNode
}) => {
  const location = useLocation()

  return (
    <Style.Page>
      <MinimalSkipLink />
      <PublicNav extra={navExtra} />
      <Style.Main id="main-content" tabIndex={-1}>
        <PageTransition key={location.pathname}>
          {children}
        </PageTransition>
      </Style.Main>
      <PublicFooter />
    </Style.Page>
  )
}
